import { useEffect, useMemo, useRef, useState } from 'react';
import { GetResponsePayload } from 'constants/payloads/partnerBookingsAvailability';
import api from 'services/client/api';
import {
  DEFAULT_TIMEZONE,
  buildDates,
  buildSlots,
  getAvailabilityEndpoint,
  getTodayLocal,
} from 'components/courtBooking';

/** One availability call per club per day; clubs that aren't live answer without a club-system call. */
export const useFeedAvailability = (venueIds: string[], localDate: string) => {
  const [days, setDays] = useState<Record<string, GetResponsePayload>>({});
  const [pendingCount, setPendingCount] = useState(0);
  // Every key asked for, in flight, failed or done, so a venue is never requested twice.
  const requestedKeys = useRef(new Set<string>());

  // Results are keyed by venue and date, so a batch the player switched away from
  // still lands and is there when they come back.
  useEffect(() => {
    const missing = venueIds.filter(
      (venueId) => !requestedKeys.current.has(`${venueId}:${localDate}`),
    );
    if (!localDate || missing.length === 0) {
      return;
    }
    missing.forEach((venueId) => requestedKeys.current.add(`${venueId}:${localDate}`));
    setPendingCount((count) => count + 1);
    Promise.all(
      missing.map((venueId) =>
        api
          .get(getAvailabilityEndpoint(venueId, localDate))
          .then((day) => [venueId, day as GetResponsePayload] as const)
          .catch(() => null),
      ),
    ).then((results) => {
      setDays((previous) => {
        const next = { ...previous };
        results.forEach((result) => {
          if (result) next[`${result[0]}:${localDate}`] = result[1];
        });
        return next;
      });
      setPendingCount((count) => count - 1);
    });
  }, [venueIds, localDate]);

  const dates = useMemo(() => buildDates(getTodayLocal(DEFAULT_TIMEZONE), null), []);

  const byVenue = useMemo(
    () =>
      Object.fromEntries(
        venueIds.flatMap((venueId) => {
          const day = days[`${venueId}:${localDate}`];
          if (!day?.isBookable) return [];
          const prices = day.courts
            .map((court) => court.fromPriceUnitAmount)
            .filter((price): price is number => price !== null);
          return [
            [
              venueId,
              {
                slots: buildSlots(day),
                fromPriceUnitAmount: prices.length ? Math.min(...prices) : null,
                currency: day.courts[0]?.currency ?? 'usd',
              },
            ],
          ];
        }),
      ),
    [venueIds, localDate, days],
  );

  return { dates, byVenue, isLoading: pendingCount > 0 };
};
